import React, { Component } from "react";
import CartContext from "./CartContext";
import { BiRefresh } from "react-icons/bi";
import { BsFillPencilFill } from "react-icons/bs";
import { MessageBox } from "./MessageBox";
import { LoadSpinner } from "./LoadSpinner";
import Fuse from "fuse.js";
import Swal from "sweetalert2";
import { Country, State, City } from "country-state-city";
import {
  OverlayTrigger,
  Tooltip,
  Button,
  ButtonToolbar,
  Form,
  Row,
  Col,
  Modal,
  Nav,
  Table,
  Navbar,
  Stack,
  FormControl,
} from "react-bootstrap";
import { FaWhatsapp } from "react-icons/fa";

export class Suppliers extends Component {
  static contextType = CartContext;
  constructor(props) {
    super(props);
    this.state = {
      suppliers: [],
      filteredSuppliers: [],
      msgboxOpen: false,
      msgboxTitle: "",
      msgboxMessage: "",
      reload: true,
      addModalShow: false,
      loading: false,
    };
    this.inputFile = React.createRef();
  }

  // Helper: determine if logged-in user is a supplier and their supplier id
  getSupplierUserContext() {
    const ctx = this.context || {};
    const user = ctx.userData || {};
    const roles = ctx.UserRoles || {};
    const isSupplierUser =
      user && roles && user.roleId === roles.SUPPLIER ? true : false;
    const supplierId = user ? user.storeRoleValue : null;
    return { isSupplierUser, supplierId };
  }

  // ---------- SEARCH ----------
  onSearchChange = (query) => {
    if (!query) {
      this.setState({ filteredSuppliers: this.state.suppliers });
      return;
    }
    const fuse = new Fuse(this.state.suppliers, {
      keys: [
        "name",
        "mobileNo",
        "city",
        "companyName",
        "gstNo",
        "enterpriseId",
      ],
      threshold: 0.2,
      ignoreLocation: true,
    });
    const result = fuse.search(query).map((r) => r.item);
    this.setState({ filteredSuppliers: result });
  };

  // ---------- API ----------
  GetSuppliers = () => {
    this.setState({ suppliers: [], filteredSuppliers: [] }, () => {
      this.setState({ loading: true });
      fetch(
        `${process.env.REACT_APP_API}Supplier/GetSuppliers/${this.context.storeId}`
      )
        .then((response) => response.json())
        .then((data) => {
          const list = Array.isArray(data) ? data : [];

          // if logged-in user is a supplier, restrict view to that supplier only
          const { isSupplierUser, supplierId } = this.getSupplierUserContext();
          const filtered = isSupplierUser
            ? list.filter(
                (s) =>
                  s &&
                  s.id &&
                  String(s.id).toLowerCase() ===
                    String(supplierId).toLowerCase()
              )
            : list;

          this.setState({
            suppliers: list,
            filteredSuppliers: filtered,
            loading: false,
          });
        })
        .catch((error) => {
          console.error("GetSuppliers error", error);
          this.setState({ loading: false });
          this.ShowMessagBox("Error", "Failed to load suppliers");
        });
    });
  };

  // ---------- CSV DOWNLOAD (client-side to include enterprise fields) ----------
  DownloadSuppliersToCSV = () => {
    const suppliers = this.state.suppliers || [];
    if (!suppliers.length) {
      this.ShowMessagBox("Information", "No suppliers to download");
      return;
    }

    const headers = [
      "Name",
      "Address",
      "City",
      "State",
      "Country",
      "PinCode",
      "MobileNo",
      "EmailId",
      "CompanyName",
      "GstNo",
      "AccountDetails",
      "IsEnterprise",
      "EnterpriseId",
    ];

    const quote = (v) => {
      if (v === null || v === undefined) return '""';
      const s = String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };

    const lines = [headers.join(",")];
    for (const s of suppliers) {
      lines.push(
        [
          quote(s.name),
          quote(s.address),
          quote(s.city),
          quote(s.state),
          quote(s.country),
          quote(s.pinCode),
          quote(s.mobileNo),
          quote(s.emailId),
          quote(s.companyName),
          quote(s.gstNo),
          quote(s.accountDetails),
          quote(s.isEnterprise === true ? "true" : "false"),
          quote(s.enterpriseId || ""),
        ].join(",")
      );
    }

    const csv = "\uFEFF" + lines.join("\r\n"); // add BOM for Excel
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const now = new Date();
    a.download = `suppliers-${now.getFullYear()}${String(
      now.getMonth() + 1
    ).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}-${String(
      now.getHours()
    ).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // ---------- CSV UPLOAD ----------
  onUploadButtonClick = () => this.inputFile.current?.click();

  // robust CSV line splitter (handles quoted fields with commas)
  splitCsvLine = (line) => {
    const result = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"' && line[i + 1] === '"') {
        // escaped quote
        cur += '"';
        i++; // skip next
      } else if (ch === '"') {
        inQuotes = !inQuotes;
      } else if (ch === "," && !inQuotes) {
        result.push(cur);
        cur = "";
      } else {
        cur += ch;
      }
    }
    result.push(cur);
    return result;
  };

  handleFileUpload = (e) => {
    const { files } = e.target;
    if (!files || !files[0]) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        await this.UploadSuppliersFromCSV(ev.target.result);
      } catch (err) {
        console.error("UploadSuppliersFromCSV error", err);
        this.ShowMessagBox("Error", "Upload failed - invalid CSV");
      }
    };
    reader.readAsText(files[0], "utf-8");
  };

  UploadSuppliersFromCSV = async (csvstr) => {
    if (!csvstr) throw new Error("Empty CSV");
    const rows = csvstr.split(/\r\n|\n/).filter((r) => r.trim().length > 0);
    if (rows.length <= 1) {
      this.ShowMessagBox("Information", "No data rows found in CSV");
      return;
    }

    // header processing (case-insensitive)
    const header = this.splitCsvLine(rows[0]).map((h) =>
      h.trim().toLowerCase()
    );
    const mapIndex = (name) => header.indexOf(name.toLowerCase());

    const nameIdx = mapIndex("name");
    const addressIdx = mapIndex("address");
    const cityIdx = mapIndex("city");
    const stateIdx = mapIndex("state");
    const countryIdx = mapIndex("country");
    const pinIdx = mapIndex("pincode");
    const mobileIdx =
      mapIndex("mobileno") >= 0 ? mapIndex("mobileno") : mapIndex("mobile");
    const emailIdx = mapIndex("emailid");
    const companyIdx = mapIndex("companyname");
    const gstIdx = mapIndex("gstno");
    const accIdx = mapIndex("accountdetails");
    const isEnterpriseIdx = mapIndex("isenterprise");
    const enterpriseIdIdx = mapIndex("enterpriseid");

    // iterate rows and post each supplier to AddOrUpdateSupplier
    let added = 0;
    for (let i = 1; i < rows.length; i++) {
      const line = rows[i];
      if (!line.trim()) continue;
      const values = this.splitCsvLine(line);

      const supplier = {
        id: undefined,
        storeId: this.context.storeId,
        name: (nameIdx >= 0 ? values[nameIdx] || "" : "")
          .trim()
          .replace(/^"|"$/g, ""),
        address: (addressIdx >= 0 ? values[addressIdx] || "" : "")
          .trim()
          .replace(/^"|"$/g, ""),
        city: (cityIdx >= 0 ? values[cityIdx] || "" : "")
          .trim()
          .replace(/^"|"$/g, ""),
        state: (stateIdx >= 0 ? values[stateIdx] || "" : "")
          .trim()
          .replace(/^"|"$/g, ""),
        country: (countryIdx >= 0 ? values[countryIdx] || "" : "")
          .trim()
          .replace(/^"|"$/g, ""),
        pinCode: (pinIdx >= 0 ? values[pinIdx] || "" : "")
          .trim()
          .replace(/^"|"$/g, ""),
        mobileNo: (mobileIdx >= 0 ? values[mobileIdx] || "" : "")
          .trim()
          .replace(/^"|"$/g, ""),
        emailId: (emailIdx >= 0 ? values[emailIdx] || "" : "")
          .trim()
          .replace(/^"|"$/g, ""),
        companyName: (companyIdx >= 0 ? values[companyIdx] || "" : "")
          .trim()
          .replace(/^"|"$/g, ""),
        gstNo: (gstIdx >= 0 ? values[gstIdx] || "" : "")
          .trim()
          .replace(/^"|"$/g, ""),
        accountDetails: (accIdx >= 0 ? values[accIdx] || "" : "")
          .trim()
          .replace(/^"|"$/g, ""),
        isEnterprise:
          isEnterpriseIdx >= 0
            ? String((values[isEnterpriseIdx] || "").trim()).toLowerCase() ===
              "true"
            : false,
        enterpriseId:
          enterpriseIdIdx >= 0
            ? (values[enterpriseIdIdx] || "").trim().replace(/^"|"$/g, "")
            : null,
      };

      // skip rows with no name or mobile
      if (!supplier.name) continue;

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API}Supplier/AddOrUpdateSupplier/`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(supplier),
          }
        );
        if (res.ok) added++;
        else console.warn("Row not added:", await res.text());
      } catch (err) {
        console.error("Error adding supplier row", err);
      }
    }

    this.ShowMessagBox("Information", `Suppliers uploaded: ${added}`);
    this.GetSuppliers();
  };

  // ---------- HELPERS ----------
  CloseMessagBoxCallback = () => this.setState({ msgboxOpen: false });
  ShowMessagBox = (title, message) =>
    this.setState({
      msgboxOpen: true,
      msgboxTitle: title,
      msgboxMessage: message,
    });
  OnRefreshButtonClick = () => {
    this.GetSuppliers();
  };

  componentDidMount = () => {
    if (!this.state.suppliers.length) this.GetSuppliers();
  };

  render() {
    const { isSupplierUser } = this.getSupplierUserContext();

    return (
      <>
        {this.state.msgboxOpen && (
          <MessageBox
            open={this.state.msgboxOpen}
            title={this.state.msgboxTitle}
            message={this.state.msgboxMessage}
            callback={this.CloseMessagBoxCallback}
          />
        )}

        <input
          style={{ display: "none" }}
          type="file"
          accept=".csv"
          ref={this.inputFile}
          onChange={this.handleFileUpload}
        />

        <Navbar className="shadow position-sticky fixed-top py-3 text-black ">
          <h3>
            <b>Suppliers</b>
          </h3>
          <Navbar.Collapse className="justify-content-end mx-0 ">
            <Nav>
              {!isSupplierUser ? (
                <ButtonToolbar>
                  <Button
                    size="sm"
                    className="mainthemecolor"
                    onClick={() => this.setState({ addModalShow: true })}
                  >
                    + Add
                  </Button>
                  &nbsp;
                  <Button
                    size="sm"
                    className="mainthemecolor"
                    onClick={this.OnRefreshButtonClick}
                  >
                    <BiRefresh size="16" /> Refresh
                  </Button>
                  &nbsp;
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={this.DownloadSuppliersToCSV}
                  >
                    Download CSV
                  </Button>
                  &nbsp;
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={this.onUploadButtonClick}
                  >
                    Upload CSV
                  </Button>
                </ButtonToolbar>
              ) : (
                <div className="text-muted" style={{ fontSize: 14 }}>
                  Viewing own supplier details
                </div>
              )}
            </Nav>
          </Navbar.Collapse>
        </Navbar>

        <div className="p-3" style={{ marginTop: 10 }}>
          {!isSupplierUser && (
            <Stack direction="horizontal" gap={3}>
              <FormControl
                type="search"
                placeholder="Search by name, phone, city, GST, company or enterprise id..."
                onChange={(e) => this.onSearchChange(e.target.value)}
              />
            </Stack>
          )}

          <Table striped border hover className="mt-3 bg-white">
            <thead>
              <tr>
                <th>#</th>
                <th>Supplier Name</th>
                <th>Address</th>
                <th>City</th>
                <th>State</th>
                <th>Country</th>
                <th>PinCode</th>
                <th>Mobile</th>
                <th>Email</th>
                <th>Company</th>
                <th>GST No</th>
                <th>Account Details</th>
                <th>Type</th>
                <th>Enterprise Id</th>
                <th className="text-end">Options</th>
              </tr>
            </thead>
            <tbody>
              {[...this.state.filteredSuppliers]
                .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                .map((s, i) => {
                  const { isSupplierUser, supplierId } =
                    this.getSupplierUserContext();
                  const readOnly =
                    isSupplierUser &&
                    s &&
                    s.id &&
                    String(s.id).toLowerCase() ===
                      String(supplierId).toLowerCase();
                  return (
                    <EditSupplierInline
                      key={s.id || i}
                      index={i}
                      supplier={s}
                      OnDeleted={this.OnRefreshButtonClick}
                      readOnly={readOnly}
                    />
                  );
                })}
            </tbody>
          </Table>
        </div>

        {/* Add modal only for non-supplier users */}
        {!isSupplierUser && this.state.addModalShow && (
          <SupplierAddOrEditModal
            show={this.state.addModalShow}
            Type="Add"
            onHide={() =>
              this.setState({ addModalShow: false }, () =>
                this.OnRefreshButtonClick()
              )
            }
          />
        )}
      </>
    );
  }
}

export class EditSupplierInline extends Component {
  static contextType = CartContext;
  constructor(props) {
    super(props);
    this.state = {
      supplier: props.supplier,
      editModalShow: false,
    };
  }

  OnDelete = () => {
    fetch(
      `${process.env.REACT_APP_API}Supplier/DeleteSupplier/${this.state.supplier.id}`,
      { method: "DELETE" }
    )
      .then((res) => {
        if (res.status !== 200) {
          Swal.fire(
            "Warning",
            "Not deleted, supplier may be linked to products",
            "warning"
          );
          return;
        }
        this.props.OnDeleted();
      })
      .catch((error) => {
        console.error("Delete failed", error);
        Swal.fire("Error", "Delete failed", "error");
      });
  };

  sanitizePhoneNumber(phone) {
    if (!phone) return "";
    const digits = String(phone).replace(/\D/g, "");
    // remove any leading zeros
    return digits.replace(/^0+/, "");
  }

  // return string suitable for wa.me (prefix with country code when needed)
  getWhatsAppNumber(phone) {
    const digits = this.sanitizePhoneNumber(phone);
    if (!digits) return null;

    // If digits already look like they include country code (length > 10), return as-is
    if (digits.length > 10) return digits;

    // if 10 digits, prepend country code - use store countryCode if present, fallback to 91
    const cc = (this.context?.store?.countryCode || "91")
      .toString()
      .replace(/\D/g, "");
    return `${cc}${digits}`;
  }

  render() {
    const s = this.state.supplier || {};
    const readOnly = !!this.props.readOnly;

    return (
      <>
        {this.state.editModalShow && !readOnly && (
          <SupplierAddOrEditModal
            show={this.state.editModalShow}
            onHide={() =>
              this.setState({ editModalShow: false }, () =>
                this.props.OnDeleted()
              )
            }
            supplier={s}
            Type="Edit"
          />
        )}
        <tr style={{ height: "50px" }}>
          <td>{this.props.index + 1}</td>
          <td>{s.name}</td>
          <td
            style={{
              maxWidth: 200,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {s.address}
          </td>
          <td>{s.city}</td>
          <td>{s.state}</td>
          <td>{s.country}</td>
          <td>{s.pinCode}</td>
          <td className="text-start" style={{ whiteSpace: "nowrap" }}>
            {s.mobileNo || "—"}

            {/* whatsapp link */}
            {(() => {
              const waNum = this.getWhatsAppNumber(s.mobileNo);
              if (!waNum) return null;
              const waUrl = `https://wa.me/${waNum}`;

              return (
                <OverlayTrigger overlay={<Tooltip>Open WhatsApp</Tooltip>}>
                  <a
                    href={waUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ms-2"
                    style={{ textDecoration: "none" }}
                    title={`WhatsApp ${s.mobileNo}`}
                  >
                    <FaWhatsapp
                      size={18}
                      style={{ color: "#25D366", verticalAlign: "middle" }}
                    />
                  </a>
                </OverlayTrigger>
              );
            })()}
          </td>
          <td>{s.emailId}</td>
          <td>{s.companyName}</td>
          <td>{s.gstNo}</td>
          <td>{s.accountDetails}</td>
          <td>{s.isEnterprise ? "Enterprise" : "Independent"}</td>
          <td style={{ maxWidth: 180, wordBreak: "break-all" }}>
            {s.enterpriseId || ""}
          </td>
          <td className="text-end">
            <Stack direction="horizontal">
              {!readOnly ? (
                <>
                  <Button
                    size="sm"
                    variant="warning"
                    onClick={() => this.setState({ editModalShow: true })}
                  >
                    <BsFillPencilFill />
                  </Button>
                  &nbsp;
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() =>
                      Swal.fire({
                        title: "Delete?",
                        text: "This will delete supplier and related products",
                        showCancelButton: true,
                      }).then((r) => r.isConfirmed && this.OnDelete())
                    }
                  >
                    🗑
                  </Button>
                </>
              ) : (
                <OverlayTrigger
                  placement="left"
                  overlay={<Tooltip id={`ro-tip-${s.id}`}>Read-only</Tooltip>}
                >
                  <span className="text-muted" style={{ fontSize: 14 }}>
                    🔒 Read-only
                  </span>
                </OverlayTrigger>
              )}
            </Stack>
          </td>
        </tr>
      </>
    );
  }
}

export class SupplierAddOrEditModal extends Component {
  static contextType = CartContext;
  constructor(props) {
    super(props);

    const s = props.supplier || {};

    this.state = {
      supplier: {
        // copy existing fields if present; otherwise default values
        id: s.id || undefined,
        name: s.name || "",
        address: s.address || "",
        city: s.city || "",
        state: s.state || "",
        country: s.country || "",
        pinCode: s.pinCode || "",
        mobileNo: s.mobileNo || "",
        emailId: s.emailId || "",
        companyName: s.companyName || "",
        gstNo: s.gstNo || "",
        accountDetails: s.accountDetails || "",
        // NEW: enterprise fields
        isEnterprise:
          s.isEnterprise === true || s.isEnterprise === "true" ? true : false,
        enterpriseId: s.enterpriseId || s.enterpriseId?.toString?.() || "",
      },
      errorMessage: "",
    };
  }

  // Generic input handler (handles checkbox/radios)
  handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" ? checked : value;
    this.setState((prev) => ({
      supplier: { ...prev.supplier, [name]: val },
    }));
  };

  // quick GUID validator
  isValidGuid = (g) => {
    if (!g) return false;
    const re =
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
    return re.test(g);
  };

  handleSubmit = (e) => {
    e.preventDefault();
    const supplier = {
      ...this.state.supplier,
      // ensure backend gets storeId
      storeId: this.context.storeId,
    };

    // If enterprise selected, enterpriseId is required and must be valid GUID
    if (supplier.isEnterprise) {
      const id = (supplier.enterpriseId || "").trim();
      if (!this.isValidGuid(id)) {
        this.setState({
          errorMessage: "Enterprise Id is required and must be a valid GUID.",
        });
        return;
      }
      supplier.enterpriseId = id;
    } else {
      // ensure enterprise fields are cleared for independent suppliers
      supplier.enterpriseId = null;
    }

    // clear any prior error
    this.setState({ errorMessage: "" });

    fetch(`${process.env.REACT_APP_API}Supplier/AddOrUpdateSupplier/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(supplier),
    })
      .then((res) => {
        if (res.status === 200) {
          this.props.onHide();
        } else if (res.status === 404 || res.status === 409) {
          // existing logic: you returned NotFound earlier for duplicates; make message clearer
          alert(
            "Supplier exists or could not be added/updated. Please check details."
          );
        } else {
          alert("Failed to save supplier. Server returned: " + res.status);
        }
      })
      .catch((err) => {
        console.error("save supplier error", err);
        alert("Failed to save supplier. See console for details.");
      });
  };

  render() {
    const s = this.state.supplier;

    return (
      <Modal {...this.props} size="lg" centered backdrop="static">
        <Form onSubmit={this.handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {this.props.Type === "Edit" ? "Edit Supplier" : "Add Supplier"}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {/* Enterprise vs Independent */}
            <Form.Group>
              <Form.Label>Supplier Type</Form.Label>
              <div>
                <Form.Check
                  inline
                  label="Independent"
                  name="isEnterprise"
                  type="radio"
                  checked={!s.isEnterprise}
                  onChange={() =>
                    this.setState((prev) => ({
                      supplier: {
                        ...prev.supplier,
                        isEnterprise: false,
                        enterpriseId: "",
                      },
                    }))
                  }
                />
                <Form.Check
                  inline
                  label="Enterprise"
                  name="isEnterprise"
                  type="radio"
                  checked={s.isEnterprise}
                  onChange={() =>
                    this.setState((prev) => ({
                      supplier: { ...prev.supplier, isEnterprise: true },
                    }))
                  }
                />
              </div>
            </Form.Group>

            {/* Enterprise ID field (shown only when enterprise selected) */}
            {s.isEnterprise && (
              <Form.Group className="mt-2">
                <Form.Label>Enterprise Id (GUID) *</Form.Label>
                <div className="d-flex">
                  <Form.Control
                    name="enterpriseId"
                    value={s.enterpriseId || ""}
                    onChange={this.handleInputChange}
                    placeholder="xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx"
                    required
                  />
                </div>
                <Form.Text className="text-muted">
                  Provide the Enterprise GUID this supplier belongs to.
                </Form.Text>
              </Form.Group>
            )}
            <hr></hr>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    name="name"
                    value={s.name || ""}
                    onChange={this.handleInputChange}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    name="address"
                    value={s.address || ""}
                    onChange={this.handleInputChange}
                  />
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    name="city"
                    value={s.city || ""}
                    onChange={this.handleInputChange}
                  />
                </Form.Group>

                {/* other left-column fields (State/Country/PinCode) keep as you had */}
                <Form.Group>
                  <Form.Label column>Country *</Form.Label>

                  <Form.Select
                    type="text"
                    name="country"
                    value={s.country}
                    required
                    placeholder=""
                    autoComplete="off"
                    onChange={(e) => this.handleInputChange(e)}
                  >
                    <option value=""></option>
                    {Country.getAllCountries().map((country) => (
                      <>
                        <option value={country.isoCode}>{country.name}</option>
                      </>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>State *</Form.Label>

                  <Form.Select
                    type="text"
                    name="state"
                    value={s.state}
                    required
                    placeholder=""
                    autoComplete="off"
                    onChange={(e) => this.handleInputChange(e)}
                  >
                    <option value=""></option>
                    {State.getStatesOfCountry(s.country).map((s1) => (
                      <>
                        <option>{s1.name}</option>
                      </>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>PinCode</Form.Label>
                  <Form.Control
                    name="pinCode"
                    value={s.pinCode || ""}
                    onChange={this.handleInputChange}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>PinCode</Form.Label>
                  <Form.Control
                    name="pinCode"
                    value={s.pinCode || ""}
                    onChange={this.handleInputChange}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label>Mobile</Form.Label>
                  <Form.Control
                    name="mobileNo"
                    value={s.mobileNo || ""}
                    onChange={this.handleInputChange}
                    required
                    maxLength={10}
                  />
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    name="emailId"
                    value={s.emailId || ""}
                    onChange={this.handleInputChange}
                  />
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label>Company Name</Form.Label>
                  <Form.Control
                    name="companyName"
                    value={s.companyName || ""}
                    onChange={this.handleInputChange}
                  />
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label>GST No</Form.Label>
                  <Form.Control
                    name="gstNo"
                    value={s.gstNo || ""}
                    onChange={this.handleInputChange}
                  />
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label>Account Details</Form.Label>
                  <Form.Control
                    name="accountDetails"
                    value={s.accountDetails || ""}
                    onChange={this.handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            {this.state.errorMessage && (
              <div className="text-danger mt-2">{this.state.errorMessage}</div>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button type="submit" variant="primary">
              Save
            </Button>
            <Button variant="secondary" onClick={this.props.onHide}>
              Close
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    );
  }
}
